
import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { CheckCircle2, Sparkles, Loader2, ArrowRight, Quote } from 'lucide-react';
import { SiteContent, TransportStep } from '../types';
import { optimizeContentAI } from '../services/geminiService';

interface TransportProps {
  data: SiteContent['transport'];
  isAdmin: boolean;
  onUpdate: (data: SiteContent['transport']) => void;
}

const Transport: React.FC<TransportProps> = ({ data, isAdmin, onUpdate }) => {
  const [isOptimizing, setIsOptimizing] = useState<string | null>(null);

  const handleOptimize = async (field: string) => {
    setIsOptimizing(field);
    const textToOptimize = field === 'proTip' ? data.proTip : data.description;
    const optimized = await optimizeContentAI(textToOptimize, `Transport section ${field}`);
    onUpdate({ ...data, [field]: optimized });
    setIsOptimizing(null);
  };

  const updateStep = (id: string, updates: Partial<TransportStep>) => {
    onUpdate({
      ...data,
      steps: data.steps.map(step => step.id === id ? { ...step, ...updates } : step)
    });
  };

  const titleWords = data.title.split(' ');
  const mainTitle = titleWords.length > 0 ? titleWords[0] : 'Getting';
  const italicTitle = titleWords.length > 1 ? titleWords.slice(1).join(' ') : 'to Paradise';

  return (
    <section id="guide" className="relative py-24 md:py-32 bg-white overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-20 lg:gap-32 items-start">
          
          {/* Left Column: Heading and Curator Note */}
          <div className="w-full lg:w-5/12 lg:sticky lg:top-32">
            <span className="text-sky-500 font-bold tracking-luxury uppercase text-[9px] mb-8 block">The Passage</span>
            
            <div className="mb-20">
              {isAdmin ? (
                <div className="p-8 border-2 border-dashed border-slate-100 rounded-[2.5rem] space-y-4 bg-slate-50/50">
                  <input 
                    value={data.title}
                    onChange={e => onUpdate({...data, title: e.target.value})}
                    className="w-full text-4xl font-bold text-slate-900 font-serif italic focus:outline-none bg-transparent"
                  />
                  <textarea 
                    value={data.description}
                    onChange={e => onUpdate({...data, description: e.target.value})}
                    className="w-full text-slate-500 text-xl focus:outline-none bg-transparent h-32 font-light leading-relaxed"
                  />
                </div>
              ) : (
                <>
                  <h2 className="text-5xl md:text-7xl font-serif font-bold text-slate-900 leading-[1.1] tracking-tight mb-10">
                    {mainTitle} <span className="block italic font-light text-sky-500 mt-2">{italicTitle}</span>
                  </h2>
                  <p className="text-slate-500 text-xl md:text-2xl leading-relaxed font-light max-w-sm mb-16 opacity-80">
                    {data.description}
                  </p>
                </>
              )}
            </div>

            {/* Integrated Curator's Note */}
            <div className="relative border-l-2 border-sky-400/30 pl-12 md:pl-16 py-4 group">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-2xl border border-sky-100 bg-sky-50 text-sky-500">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h5 className="text-[10px] font-bold text-slate-900 uppercase tracking-luxury">Expedition Note</h5>
              </div>

              {isAdmin ? (
                <textarea 
                  value={data.proTip}
                  onChange={e => onUpdate({...data, proTip: e.target.value})}
                  className="w-full bg-transparent text-slate-800 font-serif italic text-2xl focus:outline-none leading-relaxed h-32"
                />
              ) : (
                <p className="text-2xl md:text-3xl text-slate-800 font-serif italic leading-relaxed tracking-tight">
                  "{data.proTip}"
                </p>
              )}
              
              <Quote className="absolute -left-6 top-0 w-24 h-24 text-slate-50 -z-10 group-hover:text-sky-50 transition-colors duration-1000" strokeWidth={0.2} />
            </div>
          </div>

          {/* Right Column: Steps */}
          <div className="w-full lg:w-7/12 space-y-24 lg:space-y-40 pt-12">
            {data?.steps?.map((step) => {
              const Icon = (LucideIcons as any)[step.icon] || LucideIcons.MapPin;
              return (
                <div key={step.id} className="flex flex-col md:flex-row gap-12 md:gap-20 group">
                  <div className="relative flex-shrink-0">
                    <div className="w-24 h-24 md:w-36 md:h-36 bg-slate-50 rounded-[3rem] border border-slate-100 flex items-center justify-center relative z-10 transition-all group-hover:scale-110 group-hover:bg-sky-500 group-hover:border-sky-500 group-hover:shadow-2xl duration-700">
                      {isAdmin ? (
                        <input 
                          value={step.icon}
                          onChange={e => updateStep(step.id, { icon: e.target.value })}
                          className="bg-transparent text-sky-500 text-[10px] font-bold text-center w-full focus:outline-none uppercase"
                        />
                      ) : (
                        <Icon className="text-sky-500 w-10 h-10 md:w-14 md:h-14 group-hover:text-white transition-colors duration-700" strokeWidth={1} />
                      )}
                    </div>
                    {/* Line connector for visual hierarchy */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-px h-24 md:h-40 bg-slate-100 -z-10 hidden md:block"></div>
                  </div>
                  
                  <div className="flex-1 pt-4">
                    {isAdmin ? (
                      <div className="space-y-4">
                        <input 
                          value={step.title}
                          onChange={e => updateStep(step.id, { title: e.target.value })}
                          className="w-full bg-transparent text-3xl font-bold text-slate-900 font-serif italic focus:outline-none"
                        />
                        <textarea 
                          value={step.description}
                          onChange={e => updateStep(step.id, { description: e.target.value })}
                          className="w-full bg-transparent text-slate-500 text-lg leading-relaxed focus:outline-none h-24 font-light"
                        />
                      </div>
                    ) : (
                      <>
                        <h4 className="text-3xl md:text-5xl font-bold mb-8 text-slate-900 font-serif italic tracking-tight group-hover:text-sky-500 transition-colors duration-700">
                          {step.title}
                        </h4>
                        <p className="text-slate-500 text-lg md:text-xl leading-relaxed font-light mb-12 max-w-2xl opacity-80 group-hover:opacity-100 transition-opacity duration-700">
                          {step.description}
                        </p>
                        <div className="h-[2px] w-16 bg-sky-500/10 transition-all duration-1000 group-hover:w-full group-hover:bg-sky-500/30"></div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
      
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky-50 blur-[150px] rounded-full opacity-30 -z-10"></div>
    </section>
  );
};

export default Transport;
